import {Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ImageCroppedEvent, ImageCropperComponent} from "ngx-image-cropper";
import {HttpClient} from "@angular/common/http";

@Component({
  selector: 'app-upload-crop-imagem',
  templateUrl: './upload-crop-imagem.component.html',
  styleUrls: ['./upload-crop-imagem.component.scss']
})
export class UploadCropImagemComponent implements OnInit, OnDestroy {
  // Todas as instâncias vivas, em ordem de criação. O colar é escutado no documento e a
  // mesma tela pode ter mais de um upload (capa + logo): o registro é o que decide quem recebe.
  private static instancias: UploadCropImagemComponent[] = [];
  // Última área de upload que o usuário tocou (mouse ou clique): é a candidata natural ao colar.
  private static ultimaAtiva: UploadCropImagemComponent = null;

  @Input() maiorPossivel  = false;
  @Input() objeto: any = {} ;
  @Input() prop = 'linkImagem';
  @Input() label  = 'Imagem';
  @Input() ratio;
  @Input() resizeWidth;
  @Input() widthExibir;
  @Input() minWidth;
  @Input() minHeight;
  @Input()  confirmarSalvar = true;
  @Input()  uploadUrl: any = '/upload/imagem';
  // A tela mãe desenha o rótulo e a foto atual; aqui fica só a área de envio e o corte.
  @Input() semCabecalho = false;
  // Colar troca a foto mesmo com uma já salva, sem precisar abrir a edição antes.
  @Input() colarParaTrocar = false;
  @Output() onEnviou = new EventEmitter();
  @ViewChild('imageCropper')  imageCropper: any;
  croppedImage: any = '';
  showCropper = false;
  backupImagem: string;
  imageChangedEvent: any;
  cortar = false;
  imageCropData: any;
  @Input() editarImagem = false;
  config: any = { ratio: 1, resizeWidth: 80, minWidth: 80 , minHeight: 80}
  salvar: any = false;
  erro: any;
  salvando: any;
  cortando: any;
  erroLoadImage: string;
  erroTamanho: string;
  maximoAreaCorte = 500;
  // pulso visual na área de upload quando a foto chega pelo ⌘V/Ctrl+V
  colando = false;
  private pulsoColarTimer: any = null;
  readonly ehMac = /Mac|iPhone|iPad|iPod/i.test(
    (typeof navigator !== 'undefined' && (navigator.platform || navigator.userAgent)) || '');
  constructor(private httpClient: HttpClient) { }
  ngOnInit() {
    UploadCropImagemComponent.instancias.push(this);
    if(this.maiorPossivel ) this.resizeWidth = this.minWidth;

    if(this.ratio) this.config.ratio = this.ratio;
    if(this.resizeWidth) this.config.resizeWidth = this.resizeWidth;
    if(this.minWidth) this.config.minWidth = this.minWidth;
    if(this.minHeight) this.config.minHeight = this.minHeight;


    this.salvar = false;
  }

  ngOnDestroy() {
    UploadCropImagemComponent.instancias = UploadCropImagemComponent.instancias.filter((c) => c !== this);
    if (UploadCropImagemComponent.ultimaAtiva === this) UploadCropImagemComponent.ultimaAtiva = null;
    if (this.pulsoColarTimer) clearTimeout(this.pulsoColarTimer);
  }

  // ⌘ no Mac, Ctrl no resto: mostrar a tecla errada é pior do que não mostrar nenhuma
  get teclaColar(): string {
    return this.ehMac ? '⌘' : 'Ctrl';
  }

  marqueComoAtiva() {
    UploadCropImagemComponent.ultimaAtiva = this;
  }

  // ⌘V/Ctrl+V em qualquer ponto da tela: quem acabou de copiar a logo cola direto, sem salvar
  // arquivo antes. Escuta no documento porque exigir foco na área quebraria o "copiei, colei".
  @HostListener('document:paste', ['$event'])
  aoColar(evento: ClipboardEvent) {
    // Todas as instâncias ouvem o mesmo colar. Depois que a primeira recebe a foto ela deixa de
    // ser alvo, e a próxima da fila passaria a ser: o preventDefault dela marca o colar como já usado.
    if (evento.defaultPrevented || !evento.clipboardData || UploadCropImagemComponent.alvoDaColagem() !== this) return;
    // num campo de texto o colar é do campo, a não ser que só venha imagem (print): aí não haveria o que colar nele
    if (this.emCampoDeTexto(evento.target) && this.trazTexto(evento.clipboardData)) return;

    const arquivo = this.imagemDaAreaDeTransferencia(evento.clipboardData);
    if (!arquivo) return;

    evento.preventDefault();
    if (this.colarParaTrocar) this.editarImagem = true;
    this.pulseAColagem();
    // o cropper lê `event.target.files[0]`, então o arquivo colado entra pelo mesmo caminho do input
    this.fileChangeEvent({target: {files: [arquivo]}});
  }

  // Quem recebe o colar: a área que o usuário tocou por último, se ainda estiver aberta;
  // senão a primeira instância da tela que esteja esperando uma foto.
  private static alvoDaColagem(): UploadCropImagemComponent {
    const ultima = UploadCropImagemComponent.ultimaAtiva;
    if (ultima && ultima.podeReceberColagem()) return ultima;

    return UploadCropImagemComponent.instancias.find((c) => c.podeReceberColagem()) || null;
  }

  podeReceberColagem(): boolean {
    const esperandoFoto = this.exibirSelecionarImagem() || this.colarParaTrocar;

    return esperandoFoto && !this.imageChangedEvent && !this.salvar && !this.cortando;
  }

  // o que o colar trouxe: print da tela (sem nome próprio) ou arquivo copiado do Finder/Explorer
  private imagemDaAreaDeTransferencia(dados: DataTransfer): File {
    const brutos: File[] = dados.files && dados.files.length
      ? Array.prototype.slice.call(dados.files)
      // Safari antigo não preenche `files` no colar; os itens continuam lá
      : Array.prototype.slice.call(dados.items || [])
        .filter((item: DataTransferItem) => item.kind === 'file')
        .map((item: DataTransferItem) => item.getAsFile())
        .filter((file: File) => !!file);

    const imagem = brutos.find((file) => /^image\//i.test(file.type || ''));
    if (!imagem) return null;

    // print da área de transferência chega como `image.png` ou sem nome: rotula para o upload
    if (!this.ehNomeGenerico(imagem.name)) return imagem;

    return new File([imagem], `imagem-colada.${this.extensaoDoTipo(imagem.type)}`, {type: imagem.type});
  }

  private ehNomeGenerico(nome: string): boolean {
    const base = (nome || '').split('.')[0].toLowerCase();

    return !base || base === 'image' || base === 'imagem' || base === 'blob';
  }

  private extensaoDoTipo(tipo: string): string {
    if (tipo === 'image/jpeg' || tipo === 'image/jpg') return 'jpg';

    return ((tipo || '').split('/')[1] || 'png').toLowerCase();
  }

  private trazTexto(dados: DataTransfer): boolean {
    return Array.prototype.indexOf.call(dados.types || [], 'text/plain') >= 0;
  }

  private emCampoDeTexto(alvo: any): boolean {
    if (!alvo || !alvo.tagName) return false;
    const tag = String(alvo.tagName).toLowerCase();

    return tag === 'textarea' || tag === 'input' || alvo.isContentEditable === true;
  }

  private pulseAColagem() {
    if (this.pulsoColarTimer) clearTimeout(this.pulsoColarTimer);
    this.colando = true;
    this.pulsoColarTimer = setTimeout(() => this.colando = false, 700);
  }

  canceleImagem(){
    this.objeto[this.prop] = this.backupImagem;
    this.imageChangedEvent = null;
    this.showCropper = null;
    this.config.resizeWidth = this.minWidth;
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
  }
  imageLoaded() {
    this.showCropper = true;
    console.log('Image loaded')
  }
  cropperReady() {
    console.log('Cropper ready')

  }
  loadImageFailed (erro: string = 'Imagem não é do tipo suportado: png, jpeg ou jpg') {
    this.erroLoadImage = erro;
    this.imageChangedEvent = null;
  }

  private obtenhaMensagemErro(erro: any, fallback: string): string {
    if (!erro) {
      return fallback;
    }

    if (typeof erro === 'string') {
      return erro;
    }

    const mensagem =
      erro?.error?.message ||
      erro?.error?.mensagem ||
      erro?.message ||
      erro?.mensagem ||
      erro?.statusText;

    if (typeof mensagem === 'string' && mensagem.trim()) {
      return mensagem.trim();
    }

    return fallback;
  }


  fileChangeEvent(event: any) {
    delete this.erroLoadImage;
    delete this.erroTamanho;
    this.backupImagem = this.objeto[this.prop];
    this.imageChangedEvent = event;

    const file = event.target.files[0];
    // o cropper 8.1 só aceita imageChangedEvent com FileList real; o colar monta um evento sintético
    this.imageCropData = file;
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const img = new Image();
      img.onload = () => {
        if(img.width < this.minWidth || img.height < this.minHeight)
          return this.loadImageFailed(`Tamanho minimo da imagem de ser de ${this.minWidth} x ${this.minHeight}`)

        if (this.maiorPossivel) {
          // Calcula o maior tamanho possível mantendo proporção quadrada
          let tamanho = Math.min(img.width, img.height);

          if(tamanho > 800)    tamanho = 800;

          // Atualiza as configurações do cropper
          // this.config.minWidth = tamanho;
          //  this.config.minHeight = tamanho;
          this.config.resizeWidth = tamanho;
        }

      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);


  }

  corteImagem() {
    delete this.erro;
    this.cortando = true;
    let fd = new FormData();

    fd.append('files', this.dataURItoBlob(this.croppedImage));

    this.httpClient.post(this.uploadUrl , fd).toPromise().then( (resp: any ) => {
      this.backupImagem = this.objeto[this.prop];
      this.objeto[this.prop] = resp.data.file;
      this.editarImagem = false;

      this.cortando = false;
      if(this.confirmarSalvar){
        this.salvar = true;
      } else {
        this.setImagemSalva();
      }

    }).catch( erro => {
      this.cortando = false;
      this.erro = this.obtenhaMensagemErro(erro, 'Não foi possível processar a imagem.');
    })
  }

  private dataURItoBlob(dataURI: string): Blob {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: mimeString });
  }

  getLinkImagem() {
    return this.objeto[this.prop]
  }

  salve() {
    if(this.salvando) return;

    this.salvando = true;
    let retorno = {
      salvou: () => {
         this.setImagemSalva();
      },
      erro: (erro) => {
         this.salvando = false;
         this.erro = this.obtenhaMensagemErro(erro, 'Não foi possível salvar a imagem.');
      }
    }
    delete this.erro;
    this.onEnviou.emit( retorno);

  }

  setImagemSalva(){
    this.salvando = false;
    this.imageChangedEvent = null;
    this.salvar = false;
  }

  canceleSalvar() {
    // a foto já foi enviada e trocada no objeto: sem voltar, a tela mostraria como salva a que foi recusada
    this.objeto[this.prop] = this.backupImagem;
    this.imageChangedEvent = null;
    this.salvar = false;
  }

  larguraMinima(){
    return (this.widthExibir || this.config.minWidth );
  }

  alturaMinima(){
    return (this.widthExibir || this.config.minHeight )
  }

  larguraExibir(){
    if(this.maiorPossivel)
       if(this.exibirSelecionarImagem() || this.salvar)
         return this.maximoAreaCorte;

    return this.larguraMinima();
  }

  alturaExibir(){
    if(this.maiorPossivel)
      if(this.exibirSelecionarImagem() || this.salvar) {
        if(this.ratio)
          return Math.round(this.larguraExibir() / this.ratio);
        return this.larguraExibir();
      }

    return this.alturaMinima();
  }

  exibirSelecionarImagem(){
    return !this.getLinkImagem() || this.editarImagem
  }
}
